import { useState } from 'react';
import PhoneInput, { isValidPhone } from '@/components/common/PhoneInput';
import { Navigate, Link } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import { BACKEND_URL } from '@/config/api';
import { UserPlus, Loader2, Eye, EyeOff, UserCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { useReferral } from '@/hooks/useReferral';
import { useFormToken } from '@/hooks/useFormToken';

/*
  Defensa frente a bots (23/10/2026, backend/antibot.py):
  - Al abrir el formulario se pide una ficha firmada al servidor; el registro solo se acepta a partir de 3 s.
  - Campo trampa «website», invisible para las personas.
  La ficha la da hooks/useFormToken (la misma para contacto y test de idoneidad).
*/
export default function DashboardRegister() {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', phone: '', website: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useDashboard();
  const { referralCode, affiliateName } = useReferral();
  const formToken = useFormToken();

  if (user) return <Navigate to="/dashboard" replace />;

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.name.trim().split(/\s+/).length < 2) { setError('Indica tu nombre y apellidos'); return; }
    if (!isValidPhone(form.phone)) { setError('Revisa el número de teléfono'); return; }
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return; }
    if (!/[A-Z]/.test(form.password)) { setError('La contraseña debe incluir al menos una mayúscula'); return; }
    if (!/[0-9]/.test(form.password)) { setError('La contraseña debe incluir al menos un número'); return; }
    if (form.password !== form.confirmPassword) { setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    let res;
    try {
      const token = await formToken();
      res = await fetch(`${BACKEND_URL}/api/dashboard/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password, phone: form.phone, referral_code: referralCode || undefined,
          form_token: token || undefined, website: form.website || undefined }),
      });
    } catch {
      setError('No hemos podido conectar con el servidor. Inténtalo de nuevo en unos segundos.');
      setLoading(false);
      return;
    }
    try {
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        // Los 422 de validación traen un array: se muestra un mensaje legible
        throw new Error(typeof d === 'string' ? d : (res.status === 422 ? 'Revisa los datos del formulario' : 'Error al registrar'));
      }
      const data = await res.json();
      sessionStorage.setItem('dashboard_token', data.token);
      sessionStorage.setItem('dashboard_user', JSON.stringify(data.user));
      // Solo en el entorno de pruebas sin servidor de correo: el backend devuelve el código para poder probar
      if (data.dev_code) sessionStorage.setItem('fhc_dev_code', data.dev_code);
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#c9a84c]/30 transition-colors disabled:opacity-50 placeholder:text-gray-500';

  return (
    <div className="min-h-screen bg-[#060d1b] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-[#c9a84c]/[0.03] rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -left-32 w-96 h-96 bg-[#c9a84c]/[0.02] rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]/60" aria-label="Volver a la web de Freedom Homes Club" data-testid="register-logo-home">
            <img src="/logo-fhc-sm.png" alt="Freedom Homes Club" className="h-16 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2 tracking-tight" data-testid="register-title">Crear cuenta</h1>
          <p className="text-gray-400 text-sm">Únete a Freedom Homes Club</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 space-y-4" data-testid="dashboard-register-form">
          {affiliateName && (
            <div className="flex items-center gap-3 bg-[#c9a84c]/[0.08] border border-[#c9a84c]/25 rounded-xl px-4 py-3" data-testid="register-referral-badge">
              <UserCheck size={18} className="text-[#c9a84c] flex-shrink-0" />
              <p className="text-sm text-gray-300">Has sido invitado por <strong className="text-[#c9a84c]">{affiliateName}</strong></p>
            </div>
          )}
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">Nombre completo</label>
            <input type="text" value={form.name} onChange={set('name')} required disabled={loading} className={inputClass} placeholder="Tu nombre y apellidos" autoComplete="name" data-testid="register-name" />
          </div>
          {/* Campo trampa: invisible y fuera del tabulador; solo lo rellena un bot */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
            <label>Web <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></label>
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">Email</label>
            <input type="email" value={form.email} onChange={set('email')} required disabled={loading} className={inputClass} placeholder="tu@email.com" data-testid="register-email" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">Teléfono</label>
            <PhoneInput value={form.phone} onChange={(v) => setForm((prev) => ({ ...prev, phone: v }))} variant="dark" rounded="rounded-xl" heightClass="h-[46px]" required disabled={loading} testId="register-phone" />
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">Contraseña</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} required disabled={loading} className={inputClass} placeholder="Mínimo 8 caracteres, una mayúscula y un número" data-testid="register-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-400 transition-colors" aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} data-testid="register-password-toggle">
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">Confirmar contraseña</label>
            <div className="relative">
              <input type={showConfirmPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={set('confirmPassword')} required disabled={loading} className={inputClass} placeholder="Repite tu contraseña" data-testid="register-confirm-password" />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-400 transition-colors" data-testid="register-confirm-password-toggle" aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={loading} className="w-full bg-[#c9a84c] hover:bg-[#b8973f] text-[#0a1628] font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-[#c9a84c]/15" data-testid="register-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={15} />}
            {loading ? 'Registrando...' : 'Crear cuenta'}
          </button>
          <p className="text-center text-sm text-gray-400 mt-3">
            ¿Ya tienes cuenta?{' '}
            <a href="/dashboard/login" className="text-[#c9a84c] hover:underline" data-testid="register-login-link">Iniciar sesión</a>
          </p>
        </form>
      </motion.div>
    </div>
  );
}
